import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Container, Typography, Box } from "@mui/material";

import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import {
  updateFormData,
  setCachedPaymentOptions,
  setCachedPriceTables,
} from "redux/reducers/criarVitrineReducer";

import {
  mapPaymentOptionForSelect,
  mapPriceTableOption,
  recalcMinimumInvoice,
} from "../utils/showcase-config-helpers";

import ConfigSection from "./components/config-section";
import PaymentConditionsSection from "./components/payment-conditions-section";
import PriceTableSection from "./components/price-table-section";
import ValuesSection from "./components/values-section";

export default function ShowcaseConfiguration() {
  const dispatch = useDispatch();
  const snackbar = useSnackbar();

  const formData = useSelector((state) => state.criarVitrine.formData);
  const currentStep = useSelector((state) => state.criarVitrine.currentStep);
  const prevStepRef = useRef(currentStep);
  const cachedPaymentByMarca = useSelector(
    (state) => state.criarVitrine.cachedPaymentByMarca,
  );
  const cachedPriceTables = useSelector(
    (state) => state.criarVitrine.cachedPriceTables,
  );

  const [paymentOptions, setPaymentOptions] = useState([]);
  const [loadingPaymentConditions, setLoadingPaymentConditions] =
    useState(false);
  const loadPaymentSeqRef = useRef(0);

  const [priceTableOptions, setPriceTableOptions] = useState([]);
  const [loadingPriceTables, setLoadingPriceTables] = useState(false);
  const loadPriceTableSeqRef = useRef(0);

  const brandId = formData.brand?.value;

  const syncMinimumInvoice = useCallback(
    (patch) => {
      const next = { ...formData, ...patch };
      const minimumInvoice = recalcMinimumInvoice(
        next.minimumValue,
        next.paymentConditions,
      );
      dispatch(
        updateFormData({
          ...patch,
          minimumInvoice,
          paymentCondition: next.paymentConditions?.[0] ?? null,
        }),
      );
    },
    [dispatch, formData],
  );

  const handlePaymentConditionsChange = (paymentConditions) => {
    syncMinimumInvoice({ paymentConditions });
  };

  const handlePriceTablesChange = (priceTables) => {
    dispatch(
      updateFormData({
        priceTables,
        priceTable: priceTables?.[0] ?? null,
      }),
    );
  };

  const handleValuesChange = (patch) => {
    if ("minimumValue" in patch || "paymentConditions" in patch) {
      syncMinimumInvoice(patch);
      return;
    }
    dispatch(updateFormData(patch));
  };

  const handleLoadPaymentConditions = useCallback(async (idMarca) => {
    if (!idMarca) {
      setPaymentOptions([]);
      return;
    }

    const seq = ++loadPaymentSeqRef.current;
    setLoadingPaymentConditions(true);

    try {
      const res = await Api.get(
        "list-b2b-payment-conditions-enabled",
        "showcaseB2B",
        `?idMarca=${idMarca}`,
        true,
      );

      if (seq !== loadPaymentSeqRef.current) return;
      if (res?.Success && Array.isArray(res.Result)) {
        const mapped = res.Result.map(mapPaymentOptionForSelect);
        setPaymentOptions(mapped);
        dispatch(setCachedPaymentOptions({ idMarca, options: mapped }));
      } else {
        setPaymentOptions([]);
      }
    } catch (error) {
      if (seq !== loadPaymentSeqRef.current) return;
      setPaymentOptions([]);
      snackbar.showMessage(
        typeof error === "string"
          ? error
          : "Não foi possível carregar as condições de pagamento.",
        { severity: "error" },
      );
    } finally {
      if (seq === loadPaymentSeqRef.current) {
        setLoadingPaymentConditions(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snackbar do contexto não entra nas deps
  }, [dispatch]);

  const handleLoadPriceTables = useCallback(async () => {
    const seq = ++loadPriceTableSeqRef.current;
    setLoadingPriceTables(true);

    try {
      const res = await Api.get(
        "list-b2b-showcase-price-tables",
        "showcaseB2B",
        "",
        true,
      );

      if (seq !== loadPriceTableSeqRef.current) return;
      if (res?.Success && Array.isArray(res.Result)) {
        const mapped = res.Result.map(mapPriceTableOption).filter(
          (row) => row.CodTabelaPreco,
        );
        setPriceTableOptions(mapped);
        dispatch(setCachedPriceTables(mapped));
        return;
      }
      setPriceTableOptions([]);
    } catch (error) {
      if (seq !== loadPriceTableSeqRef.current) return;
      setPriceTableOptions([]);
      snackbar.showMessage(
        typeof error === "string"
          ? error
          : "Não foi possível carregar as tabelas de preço.",
        { severity: "error" },
      );
    } finally {
      if (seq === loadPriceTableSeqRef.current) {
        setLoadingPriceTables(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snackbar do contexto não entra nas deps
  }, [dispatch]);

  useEffect(() => {
    if (!brandId) {
      setPaymentOptions([]);
      return;
    }

    const cached = cachedPaymentByMarca[brandId];
    if (cached?.length) {
      setPaymentOptions(cached.map(mapPaymentOptionForSelect));
      return;
    }

    handleLoadPaymentConditions(brandId);
  }, [brandId, cachedPaymentByMarca, handleLoadPaymentConditions]);

  useEffect(() => {
    if (cachedPriceTables?.length) {
      setPriceTableOptions(cachedPriceTables);
      return;
    }
    handleLoadPriceTables();
  }, [cachedPriceTables, handleLoadPriceTables]);

  useEffect(() => {
    if (prevStepRef.current === 1 && currentStep !== 1) {
      dispatch(updateFormData(formData));
    }
    prevStepRef.current = currentStep;
  }, [currentStep, dispatch, formData]);

  useEffect(() => {
    const minimumInvoice = recalcMinimumInvoice(
      formData.minimumValue,
      formData.paymentConditions,
    );
    if (minimumInvoice !== formData.minimumInvoice) {
      dispatch(updateFormData({ minimumInvoice }));
    }
  }, [
    dispatch,
    formData.minimumInvoice,
    formData.minimumValue,
    formData.paymentConditions,
  ]);

  const brandHint = useMemo(() => {
    if (brandId) return null;
    return "Selecione a marca no passo anterior para configurar condições e tabelas.";
  }, [brandId]);

  return (
    <Container maxWidth="100%" style={{ padding: 0, marginTop: "10px" }}>
      {brandHint ? (
        <Box
          mb={2}
          px={2}
          py={1.5}
          sx={{
            borderRadius: 1,
            backgroundColor: "#FFF8E1",
            border: "1px solid #FFE082",
          }}
        >
          <Typography variant="body2">{brandHint}</Typography>
        </Box>
      ) : null}

      <ConfigSection
        title="Condição de pagamento"
        description="Selecione a condição, informe o desconto (obrigatório) e adicione à lista. É necessário ao menos uma condição para avançar."
      >
        <PaymentConditionsSection
          brandId={brandId}
          paymentOptions={paymentOptions}
          loadingPaymentConditions={loadingPaymentConditions}
          paymentConditions={formData.paymentConditions ?? []}
          onChange={handlePaymentConditionsChange}
        />
      </ConfigSection>

      <ConfigSection
        title="Tabela de preço"
        description="Selecione uma ou mais tabelas de preço disponíveis para esta vitrine."
      >
        <PriceTableSection
          brandId={brandId}
          priceTableOptions={priceTableOptions}
          loadingPriceTables={loadingPriceTables}
          priceTables={formData.priceTables ?? []}
          onChange={handlePriceTablesChange}
        />
      </ConfigSection>

      <ConfigSection
        title="Valores e regras do pedido"
        description="Defina valores mínimos e como eles devem ser aplicados no pedido."
      >
        <ValuesSection
          minimumValue={formData.minimumValue}
          minimumQuantityPerOrder={formData.minimumQuantityPerOrder}
          byLine={formData.byLine}
          byOrder={formData.byOrder}
          onChange={handleValuesChange}
        />
      </ConfigSection>
    </Container>
  );
}
